<script setup>
// "New template" action + the create/edit dialog and delete confirmation, mounted in the header of
// Chatwoot's official Settings -> Templates page. Meta owns the data: we write straight through and
// ask the official page to re-sync afterwards.
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import { useWasapiModules } from 'dashboard/wasapi/modules/useWasapiModules';
import Button from 'dashboard/components-next/button/Button.vue';
import ConfirmDialog from 'dashboard/wasapi/common/ConfirmDialog.vue';
import TemplateDialog from './TemplateDialog.vue';
import templatesAPI from '../api';
import { CLOUD_PROVIDER, registerHost } from '../manager';

const emit = defineEmits(['changed']);

const { t } = useI18n();
const inboxes = useMapGetter('inboxes/getInboxes');
const { isEnabled } = useWasapiModules();

const dialogRef = ref(null);
const confirmRef = ref(null);
const inboxId = ref(null);
const isSaving = ref(false);
const isUploading = ref(false);
const saveError = ref('');

const cloudInboxes = computed(() =>
  (inboxes.value || []).filter(
    inbox =>
      inbox.channel_type === 'Channel::Whatsapp' &&
      inbox.provider === CLOUD_PROVIDER
  )
);
const inboxOptions = computed(() =>
  cloudInboxes.value.map(inbox => ({ value: inbox.id, label: inbox.name }))
);

const errorText = error => {
  const data = error?.response?.data;
  const details = (data?.validation_errors || [])
    .map(item => t(`WASAPI_TEMPLATES.ERRORS.${item.key.toUpperCase()}`, item))
    .join(' · ');
  return [data?.error, details].filter(Boolean).join(' — ') || error.message;
};

const quota = ref(null);
const loadQuota = async () => {
  quota.value = null;
  try {
    const { data } = await templatesAPI.list(inboxId.value);
    quota.value = {
      remaining: data.meta?.remaining_creations,
      limit: data.meta?.hourly_limit,
    };
  } catch {
    quota.value = null; // no quota info is better than blocking the dialog
  }
};

const openDialog = (template = null, targetInboxId = null) => {
  saveError.value = '';
  if (targetInboxId) inboxId.value = targetInboxId;
  // creations (including duplicates) consume Meta's hourly quota; edits do not
  if (!template?.id) loadQuota();
  dialogRef.value?.open(template);
};

const onSave = async ({ template, editing }) => {
  isSaving.value = true;
  saveError.value = '';
  try {
    if (editing) {
      await templatesAPI.updateTemplate(
        inboxId.value,
        editing.id,
        template,
        editing.status
      );
      useAlert(t('WASAPI_TEMPLATES.TOAST.UPDATED'));
    } else {
      await templatesAPI.createTemplate(inboxId.value, template);
      useAlert(t('WASAPI_TEMPLATES.TOAST.CREATED'));
    }
    dialogRef.value?.close();
    emit('changed');
  } catch (error) {
    // Kept in the dialog: Meta's refusal is the whole reason the submission failed, and the
    // client needs it next to the form they have to fix.
    saveError.value = errorText(error);
  } finally {
    isSaving.value = false;
  }
};

const onUpload = async ({ file, onDone }) => {
  isUploading.value = true;
  try {
    const { data } = await templatesAPI.uploadMedia(inboxId.value, file);
    onDone(data.payload.handle);
    useAlert(t('WASAPI_TEMPLATES.TOAST.UPLOADED'));
  } catch (error) {
    useAlert(errorText(error));
  } finally {
    isUploading.value = false;
  }
};

const requestDelete = (template, targetInboxId) => {
  confirmRef.value?.open({
    title: t('WASAPI_TEMPLATES.DELETE.TITLE'),
    description: t('WASAPI_TEMPLATES.DELETE.DESCRIPTION', {
      name: template.name,
    }),
    confirmLabel: t('WASAPI_TEMPLATES.DELETE.CONFIRM'),
    onConfirm: async () => {
      try {
        await templatesAPI.deleteTemplate(
          targetInboxId,
          template.id,
          template.name
        );
        useAlert(t('WASAPI_TEMPLATES.TOAST.DELETED'));
        emit('changed');
      } catch (error) {
        useAlert(errorText(error));
      }
    },
  });
};

// The inboxes store loads after mount; default to the first cloud inbox as soon as it exists.
watch(
  cloudInboxes,
  list => {
    if (!inboxId.value) inboxId.value = list[0]?.id || null;
  },
  { immediate: true }
);

onMounted(() => {
  registerHost({ openDialog, requestDelete });
});
</script>

<template>
  <div
    v-if="cloudInboxes.length && isEnabled('templates')"
    class="flex items-center gap-2"
  >
    <!-- The inbox choice lives inside the dialog: the settings header has no room for it. -->
    <Button
      :label="$t('WASAPI_TEMPLATES.NEW')"
      icon="i-lucide-plus"
      size="sm"
      :disabled="!inboxId"
      @click="openDialog(null)"
    />
    <TemplateDialog
      ref="dialogRef"
      :is-saving="isSaving"
      :is-uploading="isUploading"
      :api-error="saveError"
      :inbox-options="inboxOptions"
      :inbox-id="inboxId"
      :quota="quota"
      @update:inbox-id="inboxId = $event"
      @save="onSave"
      @upload="onUpload"
    />
    <ConfirmDialog ref="confirmRef" />
  </div>
</template>
